import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import UserProfile from '../index';
import Button from '../../../components/ui/Button';
import AppIcon from '../../../components/AppIcon';
import SkillsSection from './components/SkillsSection';
import StrengthsSection from './components/StrengthsSection';
import AddSkillModal from './components/AddSkillModal';
import SuggestSkillModal from './components/SuggestSkillModal';
import SkillStrengthDetailDrawer from './components/SkillStrengthDetailDrawer';
import {
  getUserSkills, addUserSkill, editUserSkill,
  getUserStrengths, endorseUserSkill, withdrawEndorsement, suppressStrength, unsuppressStrength,
} from '../../../api/Employee/skills';
import { UserSkillView, UserSkillInput, ProficiencyLevel, UserStrengthView } from '../../../models/employee';
import { Roles } from '../../../components/roles';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { useCompanyCapabilities } from '../../../hooks/useCompanyCapabilities';
import ManagedElsewhereNotice from '../../../components/capabilities/ManagedElsewhereNotice';
import { RootState } from '../../../store/redux/store/store';

/*
 * CO-447 — the local `Strength` interface that used to sit here is GONE, along with its two
 * byte-identical twins in `components/StrengthsSection.tsx` and
 * `components/SkillStrengthDetailDrawer.tsx`. The one definition is `UserStrengthView` in
 * `models/employee.ts`, which mirrors the server view model. Three copies of a shape that nothing
 * populated is how they stayed in agreement; the moment real data arrived they would have diverged.
 */

const Skills: React.FC = () => {
  const { t } = useTranslation();
  // Both buttons below write a UserSkill through the same call, so both answer to this.
  const { can, loaded: capabilitiesLoaded } = useCompanyCapabilities();
  const canAuthorSkills = capabilitiesLoaded && can('canAuthorSkills');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const role = useSelector((state: RootState) => state.user.role);
  const userId = useSelector((state: RootState) => state.user.userId);
  const selectedUser = useSelector((state: RootState) => state.user.selectedUser);
  const selectedUserManagers = useSelector((state: RootState) => state.user.selectedUserManagers);
  const subjectUserId = id ?? userId;

  const isViewingAnotherUser = !!id && id !== userId;
  const isHr = Roles.HR.includes(role);
  const isManagerOfEmployee = (selectedUserManagers ?? []).some(
    (s: any) => String(s?.id) === String(userId)
  );

  // Determine user role for permissions
  const userRole: 'employee' | 'manager' = (isHr || isManagerOfEmployee) && isViewingAnotherUser ? 'manager' : 'employee';
  /*
   * OWNER DECISION 6 (2026-08-27) — who may suppress a derived strength: THE SUBJECT THEMSELF,
   * their DIRECT MANAGER, and HR. Not Admin (`Roles.HR` is ["Hr"] only, so this client never
   * offered it to an Admin — the Admin hole was API-only and is closed server-side in
   * `SkillService.LoadStrengthForSuppressionAsync`). The NEW principal on this screen is the
   * SUBJECT: on your own profile the buttons now appear. The server is the boundary; this only
   * keeps the UI from showing a button the API would refuse, or hiding one it would allow.
   */
  const canSuppressStrengths = isViewingAnotherUser ? (isHr || isManagerOfEmployee) : true;
  // EditUserSkill also permits self-edit, so an employee may set their own level on their own profile.
  const canEditProficiency = userRole === 'manager' || !isViewingAnotherUser;

  const [proficiencyError, setProficiencyError] = useState('');
  // Reason the last add/suggest save was refused, shown inside the modal that raised it. Without
  // this the two handlers below swallowed every failure into console.error and closed the modal
  // regardless, so a 400 was indistinguishable from a successful save that added nothing.
  const [skillSaveError, setSkillSaveError] = useState('');
  const [showAddSkillModal, setShowAddSkillModal] = useState(false);
  const [showSuggestSkillModal, setShowSuggestSkillModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{ item: UserSkillView | UserStrengthView | null; type: 'skill' | 'strength' }>({
    item: null,
    type: 'skill'
  });

  // Skills data from API
  const [skills, setSkills] = useState<UserSkillView[]>([]);

  // CO-447 — real derived strengths. This was `const [strengths] = useState<Strength[]>([])`, a
  // literal empty array with NO setter and a comment saying the API would come later, so the
  // Strengths section rendered its empty state on every profile in the product since it shipped.
  const [strengths, setStrengths] = useState<UserStrengthView[]>([]);

  // CO-448 — ids this visit has endorsed. Seeded from the server's `hasEndorsedByCurrentUser` when
  // it is present; the local set is the fallback for a server build that does not send it, and for
  // the moment between an optimistic click and the reload that follows it.
  const [endorsedIds, setEndorsedIds] = useState<Set<number>>(new Set());
  const [endorsingId, setEndorsingId] = useState<number | null>(null);
  const [endorseError, setEndorseError] = useState('');
  const [strengthError, setStrengthError] = useState('');

  useEffect(() => {
    if (subjectUserId) {
      loadSkills();
      loadStrengths();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectUserId]);

  const loadSkills = async () => {
    if (!subjectUserId) return;
    setLoading(true);
    try {
      const result = await getUserSkills(subjectUserId);
      if (result.status === 200 && result.payload) {
        setSkills(result.payload);
        // Seed the local set from the server flag when the server actually sends one, so a page
        // reload does not re-offer a button that will be refused.
        const alreadyMine = result.payload.filter((s) => s.hasEndorsedByCurrentUser).map((s) => s.id);
        if (alreadyMine.length > 0) setEndorsedIds((prev) => new Set([...prev, ...alreadyMine]));
      }
    } catch (error) {
      console.error('Failed to load skills:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStrengths = async () => {
    if (!subjectUserId) return;
    try {
      const result = await getUserStrengths(subjectUserId);
      if (result.status === 200 && result.payload) {
        setStrengths(result.payload);
      }
    } catch (error) {
      // A failure here must NOT blank the skills list beside it. An empty strengths list is a real
      // and common state, so it renders the same way either way; the console line is the only
      // distinction available without inventing an error surface for a section that is often empty.
      console.error('Failed to load strengths:', error);
    }
  };

  /**
   * CO-448 — optimistic endorse. The count moves immediately and rolls back on failure, because
   * the round trip to dev is slow enough that a user clicks twice when nothing happens.
   */
  const handleEndorse = async (skillId: number) => {
    if (endorsingId) return;
    const before = skills;
    setEndorsingId(skillId);
    setEndorseError('');
    setSkills((prev) => prev.map((s) => (s.id === skillId ? { ...s, endorsementCount: (s.endorsementCount ?? 0) + 1 } : s)));
    setEndorsedIds((prev) => new Set(prev).add(skillId));
    try {
      const result = await endorseUserSkill(skillId);
      if (result.status === 200 && result.errors.length === 0) {
        await loadSkills();
        return;
      }
      setSkills(before);
      setEndorsedIds((prev) => { const next = new Set(prev); next.delete(skillId); return next; });
      setEndorseError(getApiErrorMessage(result, t, t('skills.endorse.failed')));
    } catch (error) {
      setSkills(before);
      setEndorsedIds((prev) => { const next = new Set(prev); next.delete(skillId); return next; });
      setEndorseError(getApiErrorMessage(error, t, t('skills.endorse.failed')));
    } finally {
      setEndorsingId(null);
    }
  };

  const handleWithdrawEndorsement = async (skillId: number) => {
    if (endorsingId) return;
    const before = skills;
    setEndorsingId(skillId);
    setEndorseError('');
    setSkills((prev) => prev.map((s) => (s.id === skillId ? { ...s, endorsementCount: Math.max(0, (s.endorsementCount ?? 0) - 1) } : s)));
    setEndorsedIds((prev) => { const next = new Set(prev); next.delete(skillId); return next; });
    try {
      const result = await withdrawEndorsement(skillId);
      if (result.status === 200 && result.errors.length === 0) {
        await loadSkills();
        return;
      }
      setSkills(before);
      setEndorsedIds((prev) => new Set(prev).add(skillId));
      setEndorseError(getApiErrorMessage(result, t, t('skills.endorse.failed')));
    } catch (error) {
      setSkills(before);
      setEndorsedIds((prev) => new Set(prev).add(skillId));
      setEndorseError(getApiErrorMessage(error, t, t('skills.endorse.failed')));
    } finally {
      setEndorsingId(null);
    }
  };

  /**
   * CO-449 — suppression lives on `UserStrength`, which is what the nightly detector reads.
   * See `canSuppressStrengths` above for who may call it (owner decision 6).
   */
  const handleSuppressStrength = async (strength: UserStrengthView) => {
    setStrengthError('');
    try {
      const result = await suppressStrength(strength.id);
      if (result.status === 200 && result.errors.length === 0) {
        await loadStrengths();
        return;
      }
      setStrengthError(getApiErrorMessage(result, t, t('strengths.suppress.failed')));
    } catch (error) {
      setStrengthError(getApiErrorMessage(error, t, t('strengths.suppress.failed')));
    }
  };

  const handleUnsuppressStrength = async (strength: UserStrengthView) => {
    setStrengthError('');
    try {
      const result = await unsuppressStrength(strength.id);
      if (result.status === 200 && result.errors.length === 0) {
        await loadStrengths();
        return;
      }
      setStrengthError(getApiErrorMessage(result, t, t('strengths.suppress.failed')));
    } catch (error) {
      setStrengthError(getApiErrorMessage(error, t, t('strengths.suppress.failed')));
    }
  };

  const handleSkillClick = (skill: UserSkillView) => {
    setSelectedItem({ item: skill, type: 'skill' });
    setDrawerOpen(true);
  };

  const handleStrengthClick = (strength: UserStrengthView) => {
    setSelectedItem({ item: strength, type: 'strength' });
    setDrawerOpen(true);
  };

  const handleAddSkill = async (skillData: { skillName: string; proficiency: ProficiencyLevel }) => {
    if (!subjectUserId || !canAuthorSkills) return;
    try {
      const input: UserSkillInput = {
        id: 0,
        userId: subjectUserId,
        skillName: skillData.skillName,
        proficiency: skillData.proficiency
      };
      const result = await addUserSkill(input);
      if (result.status === 200 && result.errors.length === 0) {
        setSkillSaveError('');
        setShowAddSkillModal(false);
        await loadSkills();
        return;
      }
      // Same shape as handleUpdateProficiency below: prefer the server's own explanation, fall
      // back to a localized generic. The modal STAYS OPEN so the typed skill is not thrown away.
      setSkillSaveError(getApiErrorMessage(result, t, t('skills.addFailed')));
    } catch (error) {
      const msg = getApiErrorMessage(error, t, t('skills.addFailed'));
      setSkillSaveError(msg);
    }
  };

  const handleSuggestSkill = async (skillData: { skillName: string; proficiency: ProficiencyLevel }) => {
    if (!subjectUserId || !canAuthorSkills) return;
    try {
      const input: UserSkillInput = {
        id: 0,
        userId: subjectUserId,
        skillName: skillData.skillName,
        proficiency: skillData.proficiency
      };
      const result = await addUserSkill(input);
      if (result.status === 200 && result.errors.length === 0) {
        setSkillSaveError('');
        setShowSuggestSkillModal(false);
        await loadSkills();
        return;
      }
      setSkillSaveError(getApiErrorMessage(result, t, t('skills.addFailed')));
    } catch (error) {
      const msg = getApiErrorMessage(error, t, t('skills.addFailed'));
      setSkillSaveError(msg);
    }
  };

  const handleUpdateProficiency = async (skillId: number, newProficiency: ProficiencyLevel) => {
    if (!subjectUserId) return;
    try {
      const skill = skills.find(s => s.id === skillId);
      if (!skill) return;

      const input: UserSkillInput = {
        id: skillId,
        userId: subjectUserId,
        skillName: skill.skillName,
        proficiency: newProficiency
      };
      const result = await editUserSkill(skillId, input);
      if (result.status === 200) {
        setProficiencyError('');
        await loadSkills();
      } else {
        // Without this the dropdown silently snaps back and the user is told nothing.
        setProficiencyError(getApiErrorMessage(result, t, t('skills.proficiencyUpdateFailed')));
      }
    } catch (error) {
      setProficiencyError(getApiErrorMessage(error, t, t('skills.proficiencyUpdateFailed')));
    }
  };

  return (
    <div className="flex flex-col">
      <UserProfile>
        <div className="flex-1 p-2">
          {/* Capabilities Content */}
          <div className="bg-card rounded-lg shadow-sm border border-border">
            <div className="p-6">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                <div className="flex-1">
                  <ManagedElsewhereNotice capability="canAuthorSkills" className="mb-4" />
                  <h2 className="text-xl font-bold text-foreground mb-2">{t('Capabilities')}</h2>
                  <p className="text-sm text-muted-foreground">
                    {t('Skills represent capabilities that can be developed and measured over time')}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <div className="flex flex-row justify-end gap-1 mb-0 md:mb-2">
                    {canAuthorSkills && (userRole === 'manager' ? (
                      <Button
                        size="sm"
                        onClick={() => { setSkillSaveError(''); setShowAddSkillModal(true); }}
                        data-testid="profile-add-skill-btn"
                      >
                        <AppIcon name="Plus" className="me-2 h-4 w-4" />
                        {t('Add Skill')}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSkillSaveError(''); setShowSuggestSkillModal(true); }}
                        data-testid="profile-suggest-skill-btn"
                      >
                        <AppIcon name="Lightbulb" className="me-2 h-4 w-4" />
                        {t('Suggest Skill')}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              {/* CO-431: no opacity modifiers on `destructive`. Was `border-destructive/20
                  bg-destructive/5`; brought in line so all three error banners on this page are
                  one treatment rather than two. */}
              {proficiencyError && (
                <div className="mb-4 rounded-lg border border-destructive bg-card px-4 py-3 text-sm text-destructive" data-testid="proficiency-error">
                  {proficiencyError}
                </div>
              )}

              {endorseError && (
                <div className="mb-4 rounded-lg border border-destructive bg-card px-4 py-3 text-sm text-destructive" data-testid="endorse-error">
                  {endorseError}
                </div>
              )}

              {/* Skills Section */}
              <SkillsSection
                skills={skills}
                canEditProficiency={canEditProficiency}
                onSkillClick={handleSkillClick}
                onUpdateProficiency={handleUpdateProficiency}
                canEndorse={isViewingAnotherUser}
                endorsedSkillIds={endorsedIds}
                endorsingSkillId={endorsingId}
                onEndorse={handleEndorse}
                onWithdrawEndorsement={handleWithdrawEndorsement}
              />

              {/* Divider */}
              <div className="my-8 border-t border-border" />

              {strengthError && (
                <div className="mb-4 rounded-lg border border-destructive bg-card px-4 py-3 text-sm text-destructive" data-testid="strength-error">
                  {strengthError}
                </div>
              )}

              {/* Strengths Section */}
              <StrengthsSection
                strengths={strengths}
                onStrengthClick={handleStrengthClick}
                canSuppress={canSuppressStrengths}
                onSuppress={canSuppressStrengths ? handleSuppressStrength : undefined}
                onUnsuppress={canSuppressStrengths ? handleUnsuppressStrength : undefined}
              />
            </div>
          </div>
        </div>

        {/* Modals */}
        {showAddSkillModal && (
          <AddSkillModal
            isOpen={showAddSkillModal}
            onClose={() => { setSkillSaveError(''); setShowAddSkillModal(false); }}
            onSubmit={handleAddSkill}
            submitError={skillSaveError}
          />
        )}
        {showSuggestSkillModal && (
          <SuggestSkillModal
            isOpen={showSuggestSkillModal}
            onClose={() => { setSkillSaveError(''); setShowSuggestSkillModal(false); }}
            onSubmit={handleSuggestSkill}
            submitError={skillSaveError}
          />
        )}

        {/* Skill/Strength Detail Drawer */}
        <SkillStrengthDetailDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          item={selectedItem.item}
          type={selectedItem.type}
        />
      </UserProfile>
    </div>
  );
};

export default Skills;
